import React, { useMemo, useState, useEffect } from "react"
import { createPortal } from "react-dom"
import { Menu, MenuProps } from "antd"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faEdit,
  faSync,
  faList,
  faTrash,
  faChartPie,
  faTag,
} from "@fortawesome/free-solid-svg-icons"

import useContextMenu from "features/Reporting/hooks/UI/useContextMenu"

import { TransformedTableData } from "utils/convertDataToTable"
import {
  createMenuItem,
  createCheckMenuItem,
  createIconCheckMenuItem,
} from "utils/contextMenuHelpers"

import {
  STATISTIC_TYPE,
  LEGEND_POSITION,
} from "types/ReportingDashboard/elements.types"

/**
 * Props for usePieChartContextMenu hook
 */
export interface UsePieChartContextMenuProps {
  /** Whether chart has data */
  hasData: boolean
  /** Whether in show selects mode */
  showSelects: boolean
  /** Whether in dashboard mode */
  isDashboard: boolean
  /** Loading state */
  isLoading: boolean
  /** Queued for execution state */
  isQueuedForExecution: boolean
  /** Legend position setting */
  legendPosition: LEGEND_POSITION
  /** Converted chart data */
  convertedChartData: TransformedTableData | null
  /** Selected statistic type */
  selectedStatisticType: STATISTIC_TYPE
  /** Selected dashboard categories */
  selectedDashboardCategories: string[]
  /** Whether delete is available */
  onDeleteRequest?: () => void
  /** Edit handler */
  onEdit: () => void
  /** Refresh handler */
  onRefresh: () => void
  /** Delete handler */
  onDelete: () => void
  /** Toggle arc labels handler */
  onToggleArcLabels: () => void
  /** Legend position change handler */
  onLegendPositionChange: (position: string) => void
  /** Category toggle handler */
  onCategoryToggle: (category: string) => void
}

/**
 * Hook for pie chart context menu functionality
 *
 * Provides context menu items and handlers for pie chart elements.
 * Handles main context menu and category submenu.
 *
 * @param props - Hook configuration
 * @returns Context menu handlers and components
 */
export const usePieChartContextMenu = ({
  hasData,
  showSelects,
  isDashboard,
  isLoading,
  isQueuedForExecution,
  legendPosition,
  convertedChartData,
  selectedStatisticType,
  selectedDashboardCategories,
  onDeleteRequest,
  onEdit,
  onRefresh,
  onDelete,
  onToggleArcLabels,
  onLegendPositionChange,
  onCategoryToggle,
}: UsePieChartContextMenuProps) => {
  // Submenu state - managed internally
  const [showCategoriesMenu, setShowCategoriesMenu] = useState<boolean>(false)

  // Helper function to close submenus
  const closeSubmenus = (): void => {
    setShowCategoriesMenu(false)
  }

  const contextMenuItems = useMemo((): MenuProps["items"] => {
    const items: MenuProps["items"] = []

    // Only show these items when not in showSelects mode
    if (!showSelects) {
      items.push(
        createMenuItem("refresh", faSync, "Refresh Chart", onRefresh, {
          disabled: isLoading || isQueuedForExecution,
        }),
        createMenuItem("edit", faEdit, "Edit Variable", onEdit),
        createMenuItem(
          "toggleLabels",
          faTag,
          "Toggle External Labels",
          onToggleArcLabels
        )
      )

      if (isDashboard && hasData) {
        items.push(
          createMenuItem("categories", faList, "Categories", () =>
            setShowCategoriesMenu(true)
          )
        )
      }

      items.push({
        key: "legendPosition",
        icon: <FontAwesomeIcon icon={faChartPie} />,
        label: "Legend Position",
        children: [
          createIconCheckMenuItem(
            "legend-right",
            faChartPie,
            "Right",
            legendPosition === "right",
            () => onLegendPositionChange("right")
          ),
          createIconCheckMenuItem(
            "legend-left",
            faChartPie,
            "Left",
            legendPosition === "left",
            () => onLegendPositionChange("left")
          ),
          createIconCheckMenuItem(
            "legend-top",
            faChartPie,
            "Top",
            legendPosition === "top",
            () => onLegendPositionChange("top")
          ),
          createIconCheckMenuItem(
            "legend-bottom",
            faChartPie,
            "Bottom",
            legendPosition === "bottom",
            () => onLegendPositionChange("bottom")
          ),
          createIconCheckMenuItem(
            "legend-none",
            faChartPie,
            "Hidden",
            legendPosition === "none",
            () => onLegendPositionChange("none")
          ),
        ],
      })
    } else {
      // When in showSelects mode, only show edit option
      items.push(createMenuItem("edit", faEdit, "Reset Variable", onEdit))
    }

    // Always show delete if available
    if (onDeleteRequest) {
      items.push(
        { type: "divider", key: "div-delete" },
        createMenuItem("delete", faTrash, "Delete Chart", onDelete, {
          danger: true,
        })
      )
    }

    return items
  }, [
    showSelects,
    isLoading,
    isQueuedForExecution,
    legendPosition,
    isDashboard,
    hasData,
    onRefresh,
    onEdit,
    onToggleArcLabels,
    onLegendPositionChange,
    onDeleteRequest,
    onDelete,
  ])

  const categoryMenuItems = useMemo((): MenuProps["items"] => {
    if (!isDashboard || !convertedChartData || !convertedChartData.pieChartData)
      return []

    try {
      const pieCategoryData = convertedChartData.pieChartData.categoryData || []

      let statGroup = pieCategoryData.find(
        (d) => d.statistic === selectedStatisticType
      )
      // Fallback if selectedStatisticType group is not found or empty
      if (!statGroup && pieCategoryData.length > 0) {
        statGroup =
          pieCategoryData.find((d) => d.data && d.data.length > 0) ||
          pieCategoryData[0]
      }

      if (!statGroup || !statGroup.data) return [] // No data to create menu items

      const menuItems = statGroup.data
        .filter((item) => item.id !== "Totals")
        .map((item) =>
          createCheckMenuItem(
            `cat-${item.id}`,
            String(item.id),
            selectedDashboardCategories.includes(String(item.id)),
            () => onCategoryToggle(String(item.id))
          )
        )

      return menuItems
    } catch (e) {
      console.error("Error generating category menu:", e)
      return []
    }
  }, [
    isDashboard,
    convertedChartData,
    selectedStatisticType,
    selectedDashboardCategories,
    onCategoryToggle,
  ])

  // Context menu hook
  const { handleContextMenu, position, isVisible, ContextMenu } =
    useContextMenu({
      menuItems: contextMenuItems,
      isEnabled: !!(
        (!showSelects && hasData) ||
        (showSelects && onDeleteRequest)
      ),
      additionalCloseSelectors: [".categories-submenu"],
      baseClassName: "chart-context-menu",
    })

  // Handle submenu click-outside
  useEffect(() => {
    const handleClickOutside = (e: Event): void => {
      const target = e.target as Element
      if (
        showCategoriesMenu &&
        !target.closest(".categories-submenu") &&
        !target.closest(".chart-context-menu")
      ) {
        closeSubmenus()
      }
    }
    document.addEventListener("click", handleClickOutside)
    return () => document.removeEventListener("click", handleClickOutside)
  }, [showCategoriesMenu])

  // Render submenu components
  const ContextMenus = (
    <>
      {ContextMenu}
      <PieChartSubmenu
        show={showCategoriesMenu}
        items={categoryMenuItems}
        className="categories-submenu"
        style={{ maxHeight: "300px", overflowY: "auto" }}
        position={position}
        isVisible={isVisible}
      />
    </>
  )

  return {
    handleContextMenu,
    ContextMenus,
    closeSubmenus,
  }
}

/**
 * Props for Submenu component
 */
interface SubmenuProps {
  show: boolean
  items: MenuProps["items"]
  className: string
  style?: React.CSSProperties
  position: { x: number; y: number }
  isVisible: boolean
}

/**
 * Submenu component for categories
 */
export const PieChartSubmenu: React.FC<SubmenuProps> = ({
  show,
  items,
  className,
  style = {},
  position,
  isVisible,
}) => {
  if (!show || !isVisible) return null

  // Calculate submenu position relative to viewport (since main menu is now portaled)
  const submenuElement = (
    <div
      className={`${className} chart-context-menu`}
      style={{
        position: "fixed",
        top: position.y,
        left: position.x + 203, // Offset from main menu position
        zIndex: 1001, // Higher than main menu
        backgroundColor: "white",
        border: "1px solid #ddd",
        borderRadius: "4px",
        boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
        minWidth: "180px",
        ...style,
      }}
    >
      <Menu items={items} />
    </div>
  )

  // Render submenu as portal too, to match main menu behavior
  return createPortal(submenuElement, document.body)
}

/**
 * Component wrapper that uses the hook and returns React elements
 */
const PieChartContextMenu: React.FC<UsePieChartContextMenuProps> = (props) => {
  const { ContextMenus } = usePieChartContextMenu(props)
  return <>{ContextMenus}</>
}

export default PieChartContextMenu
